import React, { createContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import {
  IBuilding,
  ICustomer,
  getSchoolByDomain,
  SCHOOL_DOMAIN,
  PROPERTY_DOMAIN,
} from 'sdk-apogee';

import { LoadingContainer } from '@components';
import useDeviceDetect from '@hooks/useDeviceDetect';
import { getSubdomain, buildUrl } from '@utils';
import { DEFAULT_THEME } from '@themes';
import { applyTheme, colorLuminance } from '@themes/utils';
import myCampusNetIcon from '@assets/icons/campus-connect.png';
// @ts-ignore
import { IClientUserType } from 'sdk-apogee';
import { useAppContext } from '@contexts/AppContext';

export type LayoutContextProps = Partial<ICustomer> & {
  showGuestLoginButton: boolean;
  authType: string;
  nacType: string;
  isMiddleware: boolean;
  isSso: boolean;
  buildings: IBuilding[];
  canManageDevices: boolean;
  casAuthUrl: string;
  chatSettings: any;
  clientUserTypes: IClientUserType[];
  customerId?: number;
  isBoldyn?: boolean;
  isMobile: boolean;
  logo: string;
  favicon: string;
  largeHeroImage?: string;
  recoveryQuestions: any[];
  subdomain: string;
  university?: string;
  usernameLabel?: string;
  passwordLabel?: string;
  welcomeMessage?: string;
  logInRedirect?: (isCaptivePortalSsoLogin?: boolean) => string;
  redirectToLogin: (func?: any) => any;
  casLoginUrl?: (url: string) => string;
  roomBasedBanEnabled?: boolean;
  setupInstructions: string;
  panSharingEnabled: boolean;
  logoText: string;
  eduOnly: boolean;
  type: string;
  changeType: (type: string) => void;
  emailSupport: string;
  testCaptiveMac: string;
  ssid?: string;
  wifiPasswordEnabled: boolean;
  isGuestType?: boolean;
  guestType?: string;
  schoolUidName?: string;
  startHereSsid?: string;
  pskEnabled?: boolean;
  isCaptivePortal: boolean;
  portalAccessUrl: any;
  setIsCaptivePortal: any;
  loginToCaptivePortal: () => string;
  guestValidationEnabled?: boolean;
};

const DEFAULT_VALUES = {
  buildings: [],
  nacType: '',
  casAuthUrl: '',
  chatSettings: { profiles: '', rootUrl: '', title: '', tenant: '' },
  clientUserTypes: [],
  isBoldyn: false,
  isMobile: false,
  logo: '',
  favicon: '',
  largeHeroImage: '',
  recoveryQuestions: [],
  subdomain: '',
  usernameLabel: '',
  panSharingEnabled: false,
  eduOnly: false,
  type: undefined,
  changeType: (type: string) => {},
  emailSupport: '',
  welcomeMessage: '',
  wifiPasswordEnabled: false,
  testCaptiveMac: null,
  isGuestType: false,
  guestType: '',
  schoolUidName: '',
  startHereSsid: '',
  pskEnabled: false,
  guestValidationEnabled: false,
};

const GREEN_PROPERTY_COLOR = '#02003F';

// @ts-ignore
const LayoutContext = createContext<LayoutContextProps>(DEFAULT_VALUES);

export type LayoutProviderProps = {
  isBoldynRoute: boolean;
  publicRoute: boolean;
};

const SUBDOMAIN = getSubdomain();

const LayoutProvider: any = ({ children, publicRoute, isBoldynRoute }: any) => {
  const history = useHistory();
  const { isMobile } = useDeviceDetect();
  const { isCaptivePortal, setIsCaptivePortal, portalAccessUrl } = useAppContext();
  const [type, setType] = useState<string | undefined>('School');
  const [isThemeApplied, setIsThemeApplied] = useState(false);

  const { data, error, isLoading, refetch, status } = useQuery({
    queryKey: ['school', SUBDOMAIN],
    queryFn: () => getSchoolByDomain({ domain: SUBDOMAIN }),
    enabled: false,
  });

  const favicon = data?.favicon;
  const logo = data?.logo;
  const isMiddleware = ['middleware', 'meraki'].includes(data?.authType || '');
  const isSso = data?.authType === 'sso';
  const isBoldyn = data?.isBoldyn;
  const isGuestType = data?.guestType !== 'none';
  const nacType = data?.nacType || '';

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const previewColor = urlParams.get('color');
  const preview = urlParams.get('preview');

  useEffect(() => {
    if (error) {
      history.push('/not-found');
    }
  }, [error, history]);

  useEffect(() => {
    const isProperty = data?.type?.toLowerCase() === 'property';
    const faviconUpdate = async () => {
      const faviconTag: any = document.getElementById('favicon');

      if (favicon) {
        faviconTag.href = favicon;
      } else if (logo) {
        faviconTag.href = logo;
      } else if (isProperty) {
        faviconTag.href = myCampusNetIcon;
      }
    };

    faviconUpdate();
  }, [favicon, logo, data]);

  useEffect(() => {
    const shouldApplyTheme =
      !isThemeApplied && ((publicRoute && !isBoldynRoute) || (!publicRoute && !isBoldynRoute));

    if (data?.type === 'Property' && type !== 'Property') {
      setType('Property');
    }

    if (type === 'Property' && !shouldApplyTheme) {
      const favicon: any = document.getElementById('favicon');
      favicon.href = myCampusNetIcon;

      const primaryColor = GREEN_PROPERTY_COLOR;
      applyTheme({
        primary: primaryColor,
        backgroundPrimary: primaryColor,
        backgroundSecondary: '#E5E5E5',
        backgroundPrimaryLighten: colorLuminance(primaryColor, -0.1),
        backgroundSecondaryLighten: colorLuminance('#E5E5E5', -0.2),
      });
      return;
    }

    if (shouldApplyTheme) {
      refetch().then((response: any) => {
        const theme = response?.data;
        if (theme) {
          setIsThemeApplied(true);
          if (preview && previewColor) {
            return applyTheme({
              primary: `#${previewColor}`,
              backgroundPrimary: `#${previewColor}`,
              backgroundSecondary: '#E5E5E5',
              backgroundPrimaryLighten: colorLuminance(`#${previewColor}`, -0.1),
              backgroundSecondaryLighten: colorLuminance('#E5E5E5', -0.2),
            });
          }

          const primaryColor = `#${theme.color}`;
          theme.color
            ? applyTheme({
                primary: primaryColor,
                backgroundPrimary: primaryColor,
                backgroundSecondary: '#E5E5E5',
                backgroundPrimaryLighten: colorLuminance(primaryColor, -0.1),
                backgroundSecondaryLighten: colorLuminance('#E5E5E5', -0.2),
              })
            : applyTheme(DEFAULT_THEME);
        }
      });
    }
  }, [isBoldynRoute, publicRoute, refetch, preview, previewColor, type, data]);

  useEffect(() => {}, [preview, previewColor]);

  useEffect(() => {
    if (isBoldynRoute) return applyTheme(DEFAULT_THEME);
  }, [isBoldynRoute]);

  if (isLoading || (status === 'pending' && !publicRoute)) {
    return <LoadingContainer />;
  }

  const casLoginUrl = (url = '') => {
    const schoolDomain = encodeURIComponent(SCHOOL_DOMAIN ? `${SCHOOL_DOMAIN}` : '');
    const propertyDomain = encodeURIComponent(PROPERTY_DOMAIN ? `${PROPERTY_DOMAIN}` : '');
    const replaceExp = new RegExp(
      `(service=http[s]*%3A%2F%2F.*)${schoolDomain.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`,
    );
    if (data?.type === 'Property' && schoolDomain !== propertyDomain) {
      const casAuthUrl = data?.casAuthUrl?.replace(replaceExp, `$1${propertyDomain}`);
      return `${casAuthUrl}?return_to=${url}`;
    } else {
      return `${data?.casAuthUrl}?return_to=${url}`;
    }
  };

  const loginToCaptivePortal = () => {
    const userContinueUrl = sessionStorage.getItem('userContinueUrl') || '';
    const baseGrantUrl = sessionStorage.getItem('baseGrantUrl') || '';
    return (window.location.href = !userContinueUrl
      ? baseGrantUrl
      : `${baseGrantUrl}?continue_url=${encodeURI(userContinueUrl)}`);
  };

  const logInRedirect = (isCaptivePortalSsoLogin = false) => {
    if (isCaptivePortal) {
      if (isCaptivePortalSsoLogin) {
        const url = `${window.location.origin}/captive-portal/instructions`;
        if (casLoginUrl && typeof casLoginUrl === 'function') {
          return (window.location.href = casLoginUrl(url));
        }
      }
      return portalAccessUrl('/login');
    } else if (isMiddleware) {
      return portalAccessUrl('/login');
    }
    return casLoginUrl(buildUrl());
  };

  const redirectToLogin = (func) => {
    if (isCaptivePortal && isSso) {
      return window.location.assign((logInRedirect && logInRedirect(true)) || '');
    }
    if (!(isCaptivePortal || isMiddleware)) {
      const url = logInRedirect && logInRedirect();
      return window.location.assign(url as string);
    }

    if (isMobile || isCaptivePortal || isMiddleware) {
      return history.push(portalAccessUrl('/login'));
    }

    if (func && typeof func === 'function') {
      return func();
    }
  };

  const changeType = (newType: string) => {
    setType(newType);
  };

  const showGuestLoginButton = isGuestType && (nacType !== 'meraki' || isCaptivePortal);

  // @ts-ignore
  const props: LayoutContextProps = {
    showGuestLoginButton,
    authType: data?.authType || '',
    nacType,
    canManageDevices: nacType !== 'meraki',
    isMiddleware,
    isSso,
    buildings: data?.buildings || [],
    casAuthUrl: data?.casAuthUrl || '',
    // @ts-ignore
    chatSettings: data?.chatSettings || { profiles: '', rootUrl: '', title: '', tenant: '' },
    clientUserTypes: data?.clientUserTypes || [],
    customerId: data?.id,
    hasMultipleSites: data?.hasMultipleSites,
    isBoldyn,
    isMobile,
    largeHeroImage:
      data?.largeHeroImage?.split('/').pop() !== 'null'
        ? data?.largeHeroImage || undefined
        : undefined,
    logo: data?.logo || '',
    favicon: data?.favicon || '',
    recoveryQuestions: data?.recoveryQuestions || [],
    roomBasedPanEnabled: !!data?.roomBasedPanEnabled,
    subdomain: data?.subdomain || '',
    university: data?.displayName,
    textSupportMessage: data?.textSupportMessage,
    textSupportNumber: data?.textSupportNumber,
    phoneSupport: data?.phoneSupport,
    marketingEnabled: data?.marketingEnabled,
    marketingHeader: data?.marketingHeader,
    marketingImage: data?.marketingImage,
    marketingCopy: data?.marketingCopy,
    usernameLabel: data?.usernameLabel ? data?.usernameLabel : 'Username',
    passwordLabel: data?.passwordLabel ? data?.passwordLabel : 'Password',
    welcomeMessage: data?.welcomeMessage,
    logInRedirect,
    loginToCaptivePortal,
    redirectToLogin,
    casLoginUrl,
    setupInstructions: data?.setupInstructions || '',
    panSharingEnabled: !!data?.panSharingEnabled,
    logoText: data?.logoText || '',
    eduOnly: !!data?.eduOnly,
    type: type as 'Property' | 'School',
    changeType: changeType,
    emailSupport: data?.emailSupport || '',
    testCaptiveMac: data?.testCaptiveMac || sessionStorage.getItem('mac') || null || '',
    wifiPasswordEnabled: !!data?.wifiPasswordEnabled,
    ssid: data?.ssid,
    isGuestType,
    guestType: data?.guestType || '',
    schoolUidName: data?.schoolUidName || '',
    startHereSsid: data?.startHereSsid || '',
    pskEnabled: data?.pskEnabled,
    guestDuration: data?.guestDuration,
    isCaptivePortal,
    setIsCaptivePortal,
    portalAccessUrl,
    guestValidationEnabled: data?.guestValidationEnabled,
  };

  return <LayoutContext.Provider value={props}>{children}</LayoutContext.Provider>;
};

export { LayoutContext, LayoutProvider };
